/**
 * Exact arithmetic on the backend's decimal strings (`"12.5"`, `"3"`). Floats
 * would turn `0.1 + 0.2` into `0.30000000000000004`, so values are scaled to
 * integers with BigInt and written back in shortest form — the same shape the
 * backend sends.
 */

function scaleOf(values: readonly string[]): number {
  return Math.max(0, ...values.map((v) => v.split(".")[1]?.length ?? 0));
}

function toScaled(value: string, scale: number): bigint {
  const negative = value.startsWith("-");
  const [whole, fraction = ""] = value.replace(/^[-+]/, "").split(".");
  const digits = BigInt((whole || "0") + fraction.padEnd(scale, "0"));
  return negative ? -digits : digits;
}

function fromScaled(scaled: bigint, scale: number): string {
  const negative = scaled < BigInt(0);
  const digits = (negative ? -scaled : scaled).toString().padStart(scale + 1, "0");
  const whole = scale === 0 ? digits : digits.slice(0, -scale);
  const fraction = scale === 0 ? "" : digits.slice(-scale).replace(/0+$/, "");
  return `${negative ? "-" : ""}${whole}${fraction ? `.${fraction}` : ""}`;
}

/** Sum of the given amounts, skipping blanks; `null` when every one is blank. */
export function sumAmounts(values: readonly (string | null | undefined)[]): string | null {
  const present = values.filter((v): v is string => v != null && v !== "");
  if (present.length === 0) return null;
  const scale = scaleOf(present);
  return fromScaled(
    present.reduce((sum, v) => sum + toScaled(v, scale), BigInt(0)),
    scale
  );
}

/** Exact comparison of two decimals: negative when `a < b`, 0 when equal, positive when `a > b`. */
export function compareAmounts(a: string, b: string): number {
  const scale = scaleOf([a, b]);
  const diff = toScaled(a, scale) - toScaled(b, scale);
  return diff < BigInt(0) ? -1 : diff > BigInt(0) ? 1 : 0;
}

/** `plan − received`, floored at zero; `null` when there is no plan. */
export function remainingAmount(
  plan: string | null | undefined,
  received: string
): string | null {
  if (plan == null) return null;
  const scale = scaleOf([plan, received]);
  const diff = toScaled(plan, scale) - toScaled(received, scale);
  return fromScaled(diff > BigInt(0) ? diff : BigInt(0), scale);
}

/** Decimal places shown for any figure on screen (BOXWH-012). */
export const DISPLAY_DECIMALS = 2;

/**
 * Rounds a decimal to at most `digits` places, exactly (no float drift), and
 * returns it in shortest form: `"12.345"` → `"12.35"`, `"12.300"` → `"12.3"`.
 * `"half-up"` rounds away from zero on a tie; `"down"` truncates toward zero —
 * for an "available" figure that must never read higher than the real one.
 * A JS number is taken at up to 6 places to shed binary noise first. Returns
 * `null` for a blank or non-numeric value.
 */
export function roundAmount(
  value: string | number | null | undefined,
  digits: number = DISPLAY_DECIMALS,
  mode: "half-up" | "down" = "half-up"
): string | null {
  if (value == null) return null;
  const raw = typeof value === "number" ? (Number.isFinite(value) ? value.toFixed(6) : "") : value.trim();
  if (!/^[-+]?(\d+\.?\d*|\.\d+)$/.test(raw)) return null;
  const scale = Math.max(digits, raw.split(".")[1]?.length ?? 0);
  const scaled = toScaled(raw.replace(/\.$/, ""), scale);
  const negative = scaled < BigInt(0);
  const drop = BigInt(10) ** BigInt(scale - digits);
  const magnitude = negative ? -scaled : scaled;
  let kept = magnitude / drop;
  if (mode === "half-up" && (magnitude % drop) * BigInt(2) >= drop) kept += BigInt(1);
  const result = fromScaled(negative ? -kept : kept, digits);
  return result === "-0" ? "0" : result;
}

/**
 * A decimal for display: rounded to {@link DISPLAY_DECIMALS} places, integer
 * part grouped for the locale, fraction after a dot with trailing zeros
 * dropped (`"1234.500"` → `"1 234.5"` in Russian). `null` when not a number.
 */
export function formatAmount(
  value: string | number | null | undefined,
  locale: string,
  mode: "half-up" | "down" = "half-up"
): string | null {
  const rounded = roundAmount(value, DISPLAY_DECIMALS, mode);
  if (rounded == null) return null;
  const negative = rounded.startsWith("-");
  const [whole, fraction] = rounded.replace(/^-/, "").split(".");
  const grouped = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(
    BigInt(whole)
  );
  return `${negative ? "-" : ""}${grouped}${fraction ? `.${fraction}` : ""}`;
}
